const FENCE_MARKER_REGEX = /^ {0,3}([`~]{3,})/;
const LIST_MARKER_WITH_OPTIONAL_INDENT_REGEX =
  /^( *)((?:[-+*])|(?:\d+[.)]))(\s+)/;

type ListContext = {
  rawIndentation: number;
  normalizedContentIndentation: number;
};

/**
 * Remarkable requires one extra space to nest list markers after a parent
 * marker such as `1. `. Models often emit under-indented list markers; this
 * normalizes child markers without touching fenced code blocks.
 */
export function normalizeMarkdownListIndentation(markdown: string) {
  let fence: { char: string; length: number } | undefined;
  const listStack: ListContext[] = [];

  return markdown
    .split(/(\r\n|\r|\n)/)
    .map((part, index) => {
      if (index % 2 === 1) {
        return part;
      }

      const fenceMatch = part.match(FENCE_MARKER_REGEX);
      if (fenceMatch) {
        const marker = fenceMatch[1];
        const char = marker[0];

        if (!fence) {
          fence = { char, length: marker.length };
        } else if (fence.char === char && marker.length >= fence.length) {
          fence = undefined;
        }

        return part;
      }

      if (fence) {
        return part;
      }

      const listMatch = part.match(LIST_MARKER_WITH_OPTIONAL_INDENT_REGEX);
      if (!listMatch) {
        if (part.trim().length > 0) {
          const indentation = getIndentation(part);
          popClosedListContexts(listStack, indentation);
        }

        return part;
      }

      const indentation = listMatch[1].length;
      popClosedListContexts(listStack, indentation);

      const parentContext = listStack[listStack.length - 1];
      const normalizedIndentation =
        parentContext &&
        indentation > parentContext.rawIndentation &&
        indentation < parentContext.normalizedContentIndentation
          ? parentContext.normalizedContentIndentation
          : indentation;

      listStack.push({
        rawIndentation: indentation,
        normalizedContentIndentation:
          normalizedIndentation + listMatch[2].length + listMatch[3].length,
      });

      if (normalizedIndentation === indentation) {
        return part;
      }

      return `${' '.repeat(normalizedIndentation - indentation)}${part}`;
    })
    .join('');
}

function getIndentation(line: string) {
  return line.match(/^( *)/)![1].length;
}

function popClosedListContexts(listStack: ListContext[], indentation: number) {
  while (
    listStack.length > 0 &&
    indentation <= listStack[listStack.length - 1].rawIndentation
  ) {
    listStack.pop();
  }
}
